<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['cancel']);

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const today = new Date();
const viewDate = ref(new Date(today.getFullYear(), today.getMonth(), 1));
const selectedDateKey = ref(null);

const dateKey = date =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const messagesByDay = computed(() => {
  const map = {};
  props.messages.forEach(message => {
    const key = dateKey(new Date(message.scheduled_at));
    if (!map[key]) map[key] = [];
    map[key].push(message);
  });
  return map;
});

const monthLabel = computed(() =>
  viewDate.value.toLocaleDateString(resolvedLocale.value, {
    month: 'long',
    year: 'numeric',
  })
);

const weekDayLabels = computed(() => {
  const formatter = new Intl.DateTimeFormat(resolvedLocale.value, {
    weekday: 'short',
  });
  return Array.from({ length: 7 }, (_, i) =>
    formatter.format(new Date(2024, 0, i + 7))
  );
});

const gridDays = computed(() => {
  const year = viewDate.value.getFullYear();
  const month = viewDate.value.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    const key = dateKey(date);
    return {
      date,
      key,
      inMonth: date.getMonth() === month,
      isToday: key === dateKey(today),
      messages: messagesByDay.value[key] || [],
    };
  });
});

const selectedDayMessages = computed(
  () => messagesByDay.value[selectedDateKey.value] || []
);

const prevMonth = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear(),
    viewDate.value.getMonth() - 1,
    1
  );
};
const nextMonth = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear(),
    viewDate.value.getMonth() + 1,
    1
  );
};

const selectDay = day => {
  selectedDateKey.value = day.messages.length ? day.key : null;
};

const statusDotClass = status => {
  return (
    {
      pending: 'bg-n-amber-9',
      sent: 'bg-n-teal-9',
      failed: 'bg-n-ruby-9',
      cancelled: 'bg-n-slate-8',
    }[status] || 'bg-n-slate-8'
  );
};
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-bold capitalize text-n-slate-12">
        {{ monthLabel }}
      </h3>
      <div class="flex gap-1">
        <button
          class="p-1.5 rounded-md hover:bg-n-alpha-1 text-n-slate-11"
          @click="prevMonth"
        >
          <i class="i-lucide-chevron-left size-4" />
        </button>
        <button
          class="p-1.5 rounded-md hover:bg-n-alpha-1 text-n-slate-11"
          @click="nextMonth"
        >
          <i class="i-lucide-chevron-right size-4" />
        </button>
      </div>
    </div>

    <div class="grid grid-cols-7 gap-1 mb-1">
      <div
        v-for="label in weekDayLabels"
        :key="label"
        class="text-[11px] font-bold text-center uppercase text-n-slate-10 py-1"
      >
        {{ label }}
      </div>
    </div>

    <div class="grid grid-cols-7 gap-1">
      <button
        v-for="day in gridDays"
        :key="day.key"
        class="flex flex-col items-center justify-start h-16 p-1 rounded-lg border transition-colors"
        :class="[
          day.inMonth ? 'text-n-slate-12' : 'text-n-slate-9',
          day.key === selectedDateKey
            ? 'border-n-iris-8 bg-n-iris-3'
            : 'border-n-weak hover:bg-n-alpha-1',
        ]"
        @click="selectDay(day)"
      >
        <span
          class="text-xs"
          :class="{ 'font-bold text-n-iris-9': day.isToday }"
        >
          {{ day.date.getDate() }}
        </span>
        <div
          v-if="day.messages.length"
          class="flex flex-wrap justify-center gap-0.5 mt-1"
        >
          <span
            v-for="message in day.messages.slice(0, 4)"
            :key="message.id"
            class="rounded-full size-1.5"
            :class="statusDotClass(message.status)"
          />
        </div>
      </button>
    </div>

    <div v-if="selectedDayMessages.length" class="mt-4 space-y-2">
      <div
        v-for="message in selectedDayMessages"
        :key="message.id"
        class="flex items-center gap-2 px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
      >
        <span
          class="rounded-full size-2 shrink-0"
          :class="statusDotClass(message.status)"
        />
        <div class="flex-1 min-w-0">
          <p class="truncate text-n-slate-12">
            {{ message.content }}
          </p>
          <div class="flex items-center gap-2 text-xs text-n-slate-10">
            <span v-if="message.contact" class="truncate">
              {{ message.contact.name }}
            </span>
            <span v-if="message.inbox" class="flex items-center gap-1 shrink-0">
              <i
                :class="
                  getInboxIconByType(message.inbox.channel_type, null, 'line')
                "
                class="size-3"
              />
              {{ message.inbox.name }}
            </span>
          </div>
        </div>
        <span class="text-xs text-n-slate-10 shrink-0">
          {{
            new Date(message.scheduled_at).toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })
          }}
        </span>
        <button
          v-if="message.status === 'pending'"
          v-tooltip.top="t('SCHEDULED_MESSAGES.CANCEL')"
          class="text-n-slate-10 hover:text-n-ruby-11 shrink-0"
          @click="emit('cancel', message.id)"
        >
          <i class="i-lucide-x size-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
